import { Icona } from '../../components/Icona';
import { useEffect, useState } from 'react';
import { anagrafiche } from '../../api/acquisti';
import type { AnagraficaMago, ClassificazioneCliente } from '../../api/acquisti';
import { preventivi } from '../../api/preventivi';
import type { SedeConfigurata } from '../../api/preventivi';
import { useSessione } from '../sessione/ContestoSessione';

/*  Aprire un preventivo: il cliente, cosa si offre e la categoria.

    Tre campi e non venti, perché aprire un preventivo consuma un numero e il resto si
    riempie dopo, dentro la scheda: date, tariffe, ricarico, cantiere. Chiedere tutto qui
    vorrebbe dire che per segnare due righe di un'idea bisogna prima decidere il ricarico.

    Il cliente si cerca in Mago e non si scrive: il codice deve esistere là, o il
    preventivo nasce agganciato a un'anagrafica che non c'è e se ne accorge chi lo
    stampa.  */
/*  LA CATEGORIA LA PORTA IL CLIENTE, e viene da Mago.

    Qui c'erano quattro voci scritte a mano — «Impianti civili», «Impianti industriali»,
    «Manutenzione», «Altro» — che non corrispondevano a niente di quello che l'azienda
    usa: Galizzi i clienti li divide in Ditte, Privati, Condomini, Enti pubblici, Edili,
    Partecipate e Gestione calore, e quella classificazione sta gia' sull'anagrafica,
    compilata su tutti e tenuta dall'amministrazione.

    Quindi si legge da li' e si PROPONE, non si impone: scegliendo il cliente la tendina
    si compila da sola, e chi sta scrivendo il preventivo puo' comunque cambiarla —
    esiste il condominio a cui si fa un lavoro da ditta.

    Attenzione a non confonderla con la «categoria cliente» di Mago, che in Galizzi e'
    geografica (Biella sede, Vercelli, Novara) e a un preventivo non dice niente.  */

export function NuovoPreventivo({
  onCreato,
  onAnnulla,
}: {
  onCreato: (id: string) => void;
  onAnnulla: () => void;
}) {
  const [frase, setFrase] = useState('');
  const [risultati, setRisultati] = useState<AnagraficaMago[]>([]);
  const [cliente, setCliente] = useState<AnagraficaMago | null>(null);
  const [descrizione, setDescrizione] = useState('');
  const [oggetto, setOggetto] = useState('');
  const [categorie, setCategorie] = useState<ClassificazioneCliente[]>([]);
  const [categoria, setCategoria] = useState('');
  const [sedi, setSedi] = useState<SedeConfigurata[]>([]);
  const [sede, setSede] = useState<string | null>(null);
  const sessione = useSessione();
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  /*  La sede decide la sigla in coda al numero — «26-0001-NO» invece di «26-0001»: dal
      18/10/2026 il progressivo e' unico per tutte le sedi — e
      un numero, una volta consumato, non si cambia più. Si sceglie qui perché qui è
      l'unico momento in cui la scelta esiste ancora.

      Con una sede sola non c'è niente da chiedere e il campo non compare: chiedere di
      scegliere fra un'unica possibilità è un clic che non decide nulla.  */
  useEffect(() => {
    let annullato = false;
    preventivi
      .sedi()
      .then((elenco) => {
        if (annullato) return;
        setSedi(elenco);
        //  Prima la sede di chi scrive, poi la principale: chi lavora a Novara non deve
        //  ricordarsi di cambiarla ogni volta (Galizzi, 15/10/2026).
        const propria = elenco.find(
          (s) => s.codice.toUpperCase() === (sessione.codiceSede ?? '').toUpperCase(),
        );
        setSede(
          propria?.codice ?? elenco.find((s) => s.principale)?.codice ?? elenco[0]?.codice ?? null,
        );
      })
      // Senza l'elenco si va avanti lo stesso: il server usa la sede principale.
      .catch(() => undefined);
    return () => {
      annullato = true;
    };
  }, [sessione.codiceSede]);

  /*  Le categorie da Mago. Una volta sola all'apertura: sono sette e non cambiano
      mentre si compila.

      Se la chiamata fallisce la tendina resta vuota e il preventivo non si crea — la
      categoria e' obbligatoria — ed e' giusto: senza l'elenco l'unica alternativa
      sarebbe far scrivere un codice a mano, cioe' agganciare il documento a una classe
      che potrebbe non esistere.  */
  useEffect(() => {
    let annullato = false;
    anagrafiche
      .classificazioniCliente()
      .then((elenco) => !annullato && setCategorie(elenco))
      .catch((e: Error) => !annullato && setErrore(e.message));
    return () => {
      annullato = true;
    };
  }, []);

  useEffect(() => {
    const cercata = frase.trim();
    if (cercata.length < 2) {
      setRisultati([]);
      return;
    }

    // Mezzo secondo di attesa, come nella ricerca fornitori: le anagrafiche stanno su un
    // altro database e una query per lettera digitata sono dieci query per un nome.
    let annullato = false;
    const attesa = setTimeout(() => {
      anagrafiche
        .clienti(cercata)
        .then((r) => !annullato && setRisultati(r))
        .catch((e: Error) => !annullato && setErrore(e.message));
    }, 500);

    return () => {
      annullato = true;
      clearTimeout(attesa);
    };
  }, [frase]);

  async function crea() {
    //  La categoria e' obbligatoria sul documento: senza, il server la rifiuta e
    //  l'errore arriverebbe dopo aver consumato un numero.
    if (cliente === null || descrizione.trim() === '' || categoria === '') return;

    setInCorso(true);
    setErrore(null);
    try {
      const nato = await preventivi.crea({
        codiceClienteMago: cliente.codiceMago,
        descrizioneInterna: descrizione.trim(),
        codiceCategoria: categoria,
        codiceSede: sede,
        oggettoPerCliente: oggetto.trim() === '' ? null : oggetto.trim(),
      });
      onCreato(nato.id);
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  return (
    <aside className="scheda nuovo-preventivo" aria-label="Nuovo preventivo">
      <header>
        <div>
          <h2>Nuovo preventivo</h2>
          <p className="riferimenti">
            Il numero si consuma al salvataggio: aprire questa maschera e chiuderla non ne
            brucia nessuno.
          </p>
        </div>
        <button type="button" className="chiudi" onClick={onAnnulla} aria-label="Annulla">
          <Icona nome="chiudi" />
        </button>
      </header>

      <label className="campo-blocco">
        <span>Cliente</span>
        {cliente ? (
          <p className="dettaglio">
            <strong>{cliente.denominazione}</strong> · {cliente.codiceMago}
            <button type="button" className="rimuovi" onClick={() => setCliente(null)}>
              cambia
            </button>
          </p>
        ) : (
          <input
            type="search"
            placeholder="Cerca cliente in Mago…"
            value={frase}
            onChange={(e) => setFrase(e.target.value)}
          />
        )}
      </label>

      {!cliente && risultati.length > 0 && (
        <ul className="risultati">
          {risultati.map((c) => (
            <li key={c.codiceMago}>
              <button
                type="button"
                onClick={() => {
                  setCliente(c);
                  setFrase('');
                  setRisultati([]);
                  /*  La categoria la porta il cliente. Solo se lui ce l'ha: su uno non
                      classificato la tendina resta dov'era, invece di azzerarsi e far
                      ricominciare da capo chi aveva gia' scelto.  */
                  if (c.codiceClassificazione) setCategoria(c.codiceClassificazione);
                }}
              >
                <strong>{c.denominazione}</strong>
                <span>
                  {c.codiceMago}
                  {c.citta && ` · ${c.citta}`}
                  {!c.attiva && ' · non attivo in Mago'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <label className="campo-blocco">
        <span>Descrizione interna</span>
        <input
          placeholder="Come lo cerchi tu, non come lo legge il cliente"
          value={descrizione}
          onChange={(e) => setDescrizione(e.target.value)}
        />
      </label>

      {/*  L'OGGETTO PER IL CLIENTE, facoltativo, 16/10/2026: restano tutti e due i campi —
           la descrizione e' per l'elenco, l'oggetto finisce sulla carta — ma chi l'oggetto
           lo sa gia' lo scrive qui, senza aprire i parametri dopo.  */}
      <label className="campo-blocco">
        <span>Oggetto per il cliente</span>
        <input
          placeholder="Facoltativo: si puo' scrivere anche dopo, nella scheda"
          value={oggetto}
          onChange={(e) => setOggetto(e.target.value)}
        />
      </label>

      <label className="campo-blocco">
        <span>Categoria</span>
        <select value={categoria} onChange={(e) => setCategoria(e.target.value)}>
          {/*  La voce vuota c'e' finche' non si e' scelto: un preventivo che nasce nella
               prima classe dell'elenco perche' nessuno ha guardato la tendina e' peggio
               di uno che non si lascia creare.  */}
          <option value="">— scegli, o scegli il cliente —</option>
          {categorie.map((c) => (
            <option key={c.codice} value={c.codice}>
              {c.descrizione}
            </option>
          ))}
        </select>
        {cliente?.classificazione && categoria === cliente.codiceClassificazione && (
          <em className="nota">dall'anagrafica di {cliente.denominazione}</em>
        )}
      </label>

      {sedi.length > 1 && (
        <label className="campo-blocco">
          <span>Sede</span>
          <select value={sede ?? ''} onChange={(e) => setSede(e.target.value)}>
            {sedi.map((s) => (
              <option key={s.codice} value={s.codice}>
                {s.denominazione}
                {s.principale ? '' : ` · numeri ${s.codice}-…`}
              </option>
            ))}
          </select>
        </label>
      )}

      {errore && <p className="errore">{errore}</p>}

      <div className="azioni">
        <button
          type="button"
          className="primario"
          disabled={inCorso || cliente === null || descrizione.trim() === '' || categoria === ''}
          onClick={() => void crea()}
        >
          Apri il preventivo
        </button>
        <button type="button" onClick={onAnnulla} disabled={inCorso}>
          Annulla
        </button>
      </div>
    </aside>
  );
}
